import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';

export const QUESTION_TEXT_MAX_LENGTH = 1000;
export const OPTION_TEXT_MAX_LENGTH = 500;
export const QUESTION_EXPLANATION_MAX_LENGTH = 1000;

/** Per-locale question copy (N5, W7). */
export const TestQuestionTranslationSchema = z
  .object({
    text: z.string().trim().min(1).max(QUESTION_TEXT_MAX_LENGTH),
    /**
     * Shown on the **result** screen, never during the attempt.
     *
     * Optional because not every question has something to add beyond the right
     * answer, and a required field would be filled with the answer restated.
     */
    explanation: z.string().trim().min(1).max(QUESTION_EXPLANATION_MAX_LENGTH).optional(),
  })
  .strict();

export type TestQuestionTranslation = z.infer<typeof TestQuestionTranslationSchema>;

export const TestQuestionTranslationsSchema = makeTranslationsSchema(TestQuestionTranslationSchema);

/** Per-locale option copy (N5). */
export const TestQuestionOptionTranslationSchema = z
  .object({ text: z.string().trim().min(1).max(OPTION_TEXT_MAX_LENGTH) })
  .strict();

export const TestQuestionOptionTranslationsSchema = makeTranslationsSchema(
  TestQuestionOptionTranslationSchema,
);

/**
 * One option **as the member sees it while answering**.
 *
 * There is no `isCorrect` on this shape, and its absence is structural rather
 * than a filtering step somebody has to remember: the member-facing type simply
 * cannot express the field, so a mapper that tried to leak it would not compile.
 * `03-courses-discounts.md` requires that correct answers never ship to the
 * client during an attempt, and a boolean in a JSON payload is readable by
 * anyone with developer tools regardless of what the UI renders.
 */
export const TestQuestionOptionSchema = z
  .object({
    id: UuidSchema,
    sortOrder: z.number().int().nonnegative(),
    text: z.string(),
  })
  .strict();

export type TestQuestionOption = z.infer<typeof TestQuestionOptionSchema>;

/** The same option as **staff** authoring the test see it — with the answer key. */
export const TestQuestionOptionAuthoringSchema = TestQuestionOptionSchema.extend({
  isCorrect: z.boolean(),
  translations: TestQuestionOptionTranslationsSchema,
}).strict();

export type TestQuestionOptionAuthoring = z.infer<typeof TestQuestionOptionAuthoringSchema>;

/** One question as the member sees it while answering — no answer key anywhere. */
export const TestQuestionSchema = z
  .object({
    id: UuidSchema,
    testId: UuidSchema,
    sortOrder: z.number().int().nonnegative(),
    /**
     * Whether several options may be chosen.
     *
     * **Declared, never derived from the number of correct options.** Deriving it
     * would mean the client could infer "exactly one option is right" from the
     * control it was given — and, worse, a single-answer question would silently
     * become multi-answer the moment an author ticked a second box, changing how
     * every previous attempt would have been scored.
     */
    allowsMultiple: z.boolean(),
    text: z.string(),
    options: z.array(TestQuestionOptionSchema),
  })
  .strict();

export type TestQuestion = z.infer<typeof TestQuestionSchema>;

/** The authoring view: the answer key, the explanation, and every locale (W7). */
export const TestQuestionAuthoringSchema = TestQuestionSchema.extend({
  options: z.array(TestQuestionOptionAuthoringSchema),
  explanation: z.string().nullable(),
  translations: TestQuestionTranslationsSchema,
}).strict();

export type TestQuestionAuthoring = z.infer<typeof TestQuestionAuthoringSchema>;
